import {
  TicketChangeStatusRequest,
  ResultOfTicketChangeStatusResponse,
  TicketListDto,
  GetTicketsResponse,
} from "Api/Api";
import { SubordinateTicketItemProps } from "Components/Dashboard/SubordinatesTicketList";
import { TicketItemProps } from "Components/Dashboard/TicketListItem/TicketListItem";
import {
  ActionType,
  createAction,
  createAsyncAction,
  createReducer,
} from "typesafe-actions";

export type TicketListState = {
  tickets: TicketItemProps[];
  subordinatesTickets: SubordinateTicketItemProps[];
  isLoading: boolean;
  errorCode: string | null;
  isProcessingStatusChange: boolean;
  didCallbackUrlCallFailed: boolean;
  openedCancellation?: { ticketID: number; groupCode?: string | null };
};

export const getTicketsAsync = createAsyncAction(
  "@ticket/GET_TICKETS_REQUEST",
  "@ticket/GET_TICKETS_SUCCESS",
  "@ticket/GET_TICKETS_FAILURE",
)<
  void,
  {
    tickets: TicketListDto[];
    subordinatesTickets: SubordinateTicketItemProps[];
  },
  Error
>();

export const openSubordinateTicketsWithoutLoad = createAction(
  "OPEN_SUBORDINATES_TICKETS",
)<number>();

export const openSubordinatesTickets = createAsyncAction(
  "@ticket/OPEN_SUBORDINATES_TICKETS_REQUEST",
  "@ticket/OPEN_SUBORDINATES_TICKETS_SUCCESS",
  "@ticket/OPEN_SUBORDINATES_TICKETS_FAILURE",
)<number, GetTicketsResponse, Error>();

export const showMoreSubordinatesTickets = createAction(
  "@ticket/SHOW_MORE_SUBORDINATES_TICKETS",
)<void>();

export const closeSubordinatesTickets = createAction(
  "@ticket/CLOSE_SUBORDINATES_TICKETS",
)<number>();

export const openTicketCancel = createAction("@ticket/OPEN_TICKET_CANCEL")<{
  ticketID: number;
  groupCode?: string | null;
}>();

export const closeTicketCancel = createAction(
  "@ticket/CLOSE_TICKET_CANCEL",
)<void>();

export const hideTicket = createAction("@ticket/HIDE_TICKET")<number>();

export const changeTicketStatusAsync = createAsyncAction(
  "@ticket/CHANGE_TICKET_STATUS_REQUEST",
  "@ticket/CHANGE_TICKET_STATUS_SUCCESS",
  "@ticket/CHANGE_TICKET_STATUS_FAILURE",
)<
  TicketChangeStatusRequest,
  ResultOfTicketChangeStatusResponse,
  ResultOfTicketChangeStatusResponse
>();

export type TicketAction =
  | ActionType<typeof getTicketsAsync>
  | ActionType<typeof changeTicketStatusAsync>
  | ActionType<typeof openSubordinatesTickets>
  | ActionType<typeof openTicketCancel>
  | ActionType<typeof closeTicketCancel>
  | ActionType<typeof hideTicket>
  | ActionType<typeof showMoreSubordinatesTickets>
  | ActionType<typeof closeSubordinatesTickets>
  | ActionType<typeof openSubordinateTicketsWithoutLoad>;

export const ticketReducer = createReducer<TicketListState, TicketAction>({
  tickets: [],
  subordinatesTickets: [],
  isLoading: false,
  errorCode: null,
  isProcessingStatusChange: false,
  didCallbackUrlCallFailed: false,
})
  .handleAction(getTicketsAsync.request, state => {
    return { ...state, isLoading: true };
  })
  .handleAction(getTicketsAsync.success, (state, action) => {
    return {
      ...state,
      tickets: action.payload.tickets,
      subordinatesTickets: action.payload.subordinatesTickets,
      errorCode: null,
      isLoading: false,
    };
  })
  .handleAction(getTicketsAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })
  .handleAction(changeTicketStatusAsync.request, (state, action) => {
    return {
      ...state,
      isProcessingStatusChange: true,
      tickets: state.tickets.map(e => {
        e.isProcessing = e.ticketID === action.payload.ticketID;
        e.isDisabled = !e.isProcessing;

        return e;
      }),
    };
  })
  .handleAction(changeTicketStatusAsync.success, (state, action) => {
    return {
      ...state,
      isProcessingStatusChange: false,
      openedCancellation: undefined,
      tickets: disableTicketsProcessing(
        action.payload.isValid
          ? state.tickets.filter(
              e => e.ticketID !== action.payload.data?.ticketID,
            )
          : state.tickets,
      ),
    };
  })
  .handleAction(changeTicketStatusAsync.failure, (state, action) => {
    return {
      ...state,
      isProcessingStatusChange: false,
      openedCancellation: undefined,
      tickets: disableTicketsProcessing(state.tickets),
      errorCode: action.payload.validationErrors
        ? action.payload.validationErrors[0].message
        : "500",
    };
  })
  .handleAction(openSubordinateTicketsWithoutLoad, (state, action) => {
    return {
      ...state,
      subordinatesTickets: state.subordinatesTickets.map(e => {
        if (e.employeeID === action.payload) {
          e.isOpened = true;
        }

        return e;
      }),
    };
  })
  .handleAction(openSubordinatesTickets.request, (state, action) => {
    return {
      ...state,
      subordinatesTickets: state.subordinatesTickets.map(e => {
        if (e.employeeID === action.payload) {
          e.isLoading = true;
        }

        return e;
      }),
    };
  })
  .handleAction(openSubordinatesTickets.success, (state, action) => {
    return {
      ...state,
      subordinatesTickets: state.subordinatesTickets.map(e => {
        if (e.employeeID === action.payload.employeeID) {
          e.isLoading = false;
          e.isLoaded = true;
          e.isOpened = true;
          e.tickets = action.payload.tickets;
        }

        return e;
      }),
    };
  })
  .handleAction(closeSubordinatesTickets, (state, action) => {
    return {
      ...state,
      subordinatesTickets: state.subordinatesTickets.map(e => {
        if (e.employeeID === action.payload) {
          e.isOpened = false;
        }

        return e;
      }),
    };
  })
  .handleAction(showMoreSubordinatesTickets, state => {
    let counter = 0;
    return {
      ...state,
      subordinatesTickets: state.subordinatesTickets.map(e => {
        if (!e.isDisplayed && counter < 15) {
          e.isDisplayed = true;
          counter++;
        }
        return e;
      }),
    };
  })
  .handleAction(openTicketCancel, (state, action) => {
    return {
      ...state,
      openedCancellation: action.payload,
    };
  })
  .handleAction(closeTicketCancel, state => {
    return {
      ...state,
      openedCancellation: undefined,
    };
  })
  .handleAction(hideTicket, (state, action) => {
    return {
      ...state,
      tickets: state.tickets.filter(e => e.ticketID !== action.payload),
    };
  });

const disableTicketsProcessing = (tickets: TicketItemProps[]) =>
  tickets.map(e => {
    e.isProcessing = false;
    e.isDisabled = false;

    return e;
  });
